/**
 * Logger Utility
 * 
 * Provides structured logging
 * Console logs are disabled in production environment
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  [key: string]: any;
}

class Logger {
  private isProduction: boolean;
  private enabledLevels: Set<LogLevel>;

  constructor() {
    this.isProduction = process.env.REACT_APP_BUILD_ENV === 'PROD';
    // In production, only allow errors
    this.enabledLevels = this.isProduction 
      ? new Set<LogLevel>(['error']) 
      : new Set<LogLevel>(['debug', 'info', 'warn', 'error']);
  }

  /**
   * Format log message with context
   */
  private formatMessage(level: LogLevel, message: string, context?: LogContext): void {
    if (!this.enabledLevels.has(level)) {
      return;
    }

    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`;

    if (context) {
      switch (level) {
        case 'debug':
          // eslint-disable-next-line no-console
          console.debug(prefix, message, context);
          break;
        case 'info':
          // eslint-disable-next-line no-console
          console.info(prefix, message, context);
          break;
        case 'warn':
          // eslint-disable-next-line no-console
          console.warn(prefix, message, context);
          break;
        case 'error':
          // eslint-disable-next-line no-console
          console.error(prefix, message, context);
          break;
      }
    } else {
      switch (level) {
        case 'debug':
          // eslint-disable-next-line no-console
          console.debug(prefix, message);
          break;
        case 'info':
          // eslint-disable-next-line no-console
          console.info(prefix, message);
          break;
        case 'warn':
          // eslint-disable-next-line no-console
          console.warn(prefix, message);
          break;
        case 'error':
          // eslint-disable-next-line no-console
          console.error(prefix, message);
          break;
      }
    }
  }

  /**
   * Log debug message (disabled in production)
   */
  debug(message: string, context?: LogContext): void {
    this.formatMessage('debug', message, context);
  }

  /**
   * Log info message (disabled in production)
   */
  info(message: string, context?: LogContext): void {
    this.formatMessage('info', message, context);
  }

  /**
   * Log warning message (disabled in production)
   */
  warn(message: string, context?: LogContext): void {
    this.formatMessage('warn', message, context);
  }

  /**
   * Log error message (enabled in all environments)
   */
  error(message: string, error?: Error | any, context?: LogContext): void {
    const errorContext = {
      ...context,
      error: error instanceof Error 
        ? {
            message: error.message,
            stack: error.stack,
            name: error.name
          }
        : error
    };
    
    this.formatMessage('error', message, errorContext);
  }
}

// Export singleton instance
export const logger = new Logger();

// Export for testing
export { Logger };
